import { CDN_IMG_HOST_1688, CDN_IMG_HOST_TAOBAO, CDN_IMG_HOST_WEIDIAN } from "@kupikupi/brand";

/**
 * Source-platform image CDN → Yandex CDN proxy rewrite for **display** (#826/#862).
 *
 * Search / product-result images come from the backend as source-platform CDN
 * direct links (淘宝 img.alicdn.com / 1688 cbu01.alicdn.com / 微店 si.geilicdn.com).
 * Russian end users pull those cross-border (slow, uncached, full-size). We swap
 * the host to a near-Russia Yandex CDN proxy subdomain (transparent proxy: same
 * path) and append a scene-sized thumbnail so the browser downloads a small,
 * sharp variant instead of the full-res original.
 *
 * ## Reliable alicdn suffixes only (#862, measured)
 * The alicdn image service ONLY reliably honors:
 *   - **square** `_<n>x<n>q<q>.jpg`  (e.g. _500x500q90)
 *   - **recompress-only** `_q<q>.jpg` (keeps original dimensions)
 * Width-only / tall-box forms (`_750x`, `_750x10000`, `_750x4000`…) return a 1×1
 * placeholder on cbu01 (1688) and inconsistently on img (taobao) — so they are
 * NOT usable for keep-width long images. Long (商品介绍) images therefore use
 * recompress-only `_q80` (reliable, no crop) on alicdn, and `?w=` on weidian.
 *
 * ## Per-platform sizing
 * 1688 (cbu01) originals are 1300px+ and byte-heavy (a _500x500q90 card is ~60KB
 * vs taobao's ~25KB), which on a far-CDN network turns a dense 1688 catalog grid
 * into a 20s load. So 1688's *card* is intentionally smaller than taobao's.
 *
 * | scene  | taobao (img)     | 1688 (cbu01)     | weidian (?w) |
 * |--------|------------------|------------------|--------------|
 * | card   | _400x400q80      | _300x300q80      | 400          |
 * | main   | _800x800q90      | _800x800q90      | 1080         |
 * | detail | _q80 (recompress)| _q80 (recompress)| 1080         |
 * | swatch | _120x120q90      | _120x120q90      | 120          |
 *
 * Pure host (+ scene-suffix) rewrite. Applied at the api normalization boundary
 * (`product.ts`) and at render for description/swatch images; image-search
 * reverses it via {@link originalImageUrl} so it always uploads the source URL.
 */

type Platform = "taobao" | "alibaba1688" | "weidian";
type Scene = "card" | "main" | "detail" | "swatch";

interface CdnPlatformRule {
  platform: Platform;
  /** Canonical source host restored by originalImageUrl(). */
  sourceHost: string;
  /** Source hosts this rule matches (alicdn family / cbu01 / geilicdn). */
  matchesSource: (host: string) => boolean;
  /** Yandex CDN proxy host the source host maps to. */
  cdnHost: string;
  /** alicdn = path-suffix sizing; geilicdn = `?w=` query sizing. */
  kind: "alicdn" | "geilicdn";
}

/**
 * Per-platform, per-scene size token. For `kind: "alicdn"` it is the path suffix
 * (appended after the real extension); for `kind: "geilicdn"` it is the `?w=`
 * width. Only square / recompress alicdn suffixes are listed — see file header.
 */
const SIZES: Record<Platform, Record<Scene, string>> = {
  taobao: {
    card: "_400x400q80.jpg",
    main: "_800x800q90.jpg",
    detail: "_q80.jpg",
    swatch: "_120x120q90.jpg",
  },
  alibaba1688: {
    card: "_300x300q80.jpg",
    main: "_800x800q90.jpg",
    detail: "_q80.jpg",
    swatch: "_120x120q90.jpg",
  },
  weidian: { card: "400", main: "1080", detail: "1080", swatch: "120" },
};

/**
 * Build the rule table from the env-driven CDN hosts re-exported by
 * @kupikupi/brand. Computed per-call to stay trivially tree-shakeable.
 */
function platformRules(): CdnPlatformRule[] {
  return [
    {
      platform: "taobao",
      sourceHost: "img.alicdn.com",
      matchesSource: (host) =>
        host === "img.alicdn.com" ||
        host === "alicdn.com" ||
        (host.endsWith(".alicdn.com") && host !== "cbu01.alicdn.com") ||
        host === "taobaocdn.com" ||
        host.endsWith(".taobaocdn.com") ||
        host === "tbcdn.cn" ||
        host.endsWith(".tbcdn.cn"),
      cdnHost: CDN_IMG_HOST_TAOBAO,
      kind: "alicdn",
    },
    {
      platform: "alibaba1688",
      sourceHost: "cbu01.alicdn.com",
      matchesSource: (host) => host === "cbu01.alicdn.com",
      cdnHost: CDN_IMG_HOST_1688,
      kind: "alicdn",
    },
    {
      platform: "weidian",
      sourceHost: "si.geilicdn.com",
      matchesSource: (host) => host === "si.geilicdn.com",
      cdnHost: CDN_IMG_HOST_WEIDIAN,
      kind: "geilicdn",
    },
  ];
}

const SOURCE_HOST_PARAM = "kk_src";

/**
 * Every alicdn display suffix WE emit (current + legacy). Strip matching here so
 * re-targeting between scenes is idempotent and originalImageUrl can reverse to
 * the source path. Exact-match (not a broad regex) on purpose: a generic strip
 * could eat a real segment of a source key. Add a new scene suffix above ⇒ add
 * it here too.
 */
const ALICDN_VARIANT_SUFFIXES = [
  "_500x500q90.jpg", // legacy taobao card (pre-#876) — still in cache, must be reversible
  "_300x300q80.jpg",
  "_800x800q90.jpg",
  "_120x120q90.jpg",
  "_q80.jpg",
  "_400x400q80.jpg", // current taobao card (#876) + legacy main
  "_240x240q75.jpg", // legacy card (pre-#863)
];

function stripAlicdnVariant(pathname: string): string {
  for (const suffix of ALICDN_VARIANT_SUFFIXES) {
    if (pathname.endsWith(suffix)) return pathname.slice(0, -suffix.length);
  }
  return pathname;
}

/**
 * Resolve the platform rule for a host that is EITHER a source host or one of
 * our CDN proxy hosts. `onCdn` = the URL is already rewritten (re-targeting).
 */
function findRule(host: string): { rule: CdnPlatformRule; onCdn: boolean } | null {
  for (const rule of platformRules()) {
    if (!rule.cdnHost) continue; // platform disabled via env
    if (rule.cdnHost === host) return { rule, onCdn: true };
    if (rule.matchesSource(host)) return { rule, onCdn: false };
  }
  return null;
}

/**
 * Core rewrite: host-swap (source → CDN proxy, if needed) + apply the per-platform
 * size for `scene`. Idempotent — an already-rewritten CDN URL of ANY scene is
 * re-targeted. URLs from unrecognized hosts (S3 / data: / relative / malformed)
 * are returned unchanged so we never break images we don't own.
 */
function applyVariant(url: string, scene: Scene): string {
  if (!url) return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const found = findRule(parsed.hostname);
  if (!found) return url;
  const { rule, onCdn } = found;

  if (!onCdn) {
    const sourceHost = parsed.hostname;
    parsed.hostname = rule.cdnHost;
    // Remember a non-canonical source host so originalImageUrl restores it exactly.
    if (sourceHost !== rule.sourceHost) {
      parsed.searchParams.set(SOURCE_HOST_PARAM, sourceHost);
    }
  }

  const size = SIZES[rule.platform][scene];
  if (rule.kind === "alicdn") {
    parsed.pathname = stripAlicdnVariant(parsed.pathname) + size;
  } else {
    parsed.searchParams.set("w", size);
  }
  return parsed.toString();
}

/** Catalog / home / favorites / recommend cards (dense grid, ≤294px). */
export function toCatalogCardImageUrl(url: string): string {
  return applyVariant(url, "card");
}

/** PDP main image + gallery (≤520px, retina-sharp). */
export function toProductMainImageUrl(url: string): string {
  return applyVariant(url, "main");
}

/**
 * PDP 商品介绍 long rich-description images. alicdn cannot reliably width-resize
 * via URL suffix (see header), so this recompresses only (`_q80`, keeps the
 * original dimensions, never crops); weidian honors `?w=`. Source originals that
 * are already a sane width shrink nicely; oversized 1688 originals stay large
 * (a true width cap there needs a resize-capable CDN — tracked separately).
 */
export function toProductDetailImageUrl(url: string): string {
  return applyVariant(url, "detail");
}

/** SKU color/variant swatch (~48px). */
export function toSkuSwatchImageUrl(url: string): string {
  return applyVariant(url, "swatch");
}

/**
 * @deprecated Back-compat alias — equals the PDP main variant. Prefer the
 * explicit scene helper. Kept so existing imports keep compiling.
 */
export function toCdnImageUrl(url: string): string {
  return applyVariant(url, "main");
}

/** True when the URL already points at one of our platform image CDN hosts. */
export function isCdnImageUrl(url: string): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname;
    return platformRules().some((r) => r.cdnHost && r.cdnHost === host);
  } catch {
    return false;
  }
}

/**
 * Reverse any scene rewrite: map a CDN proxy host back to the source host and
 * strip the display thumbnail (alicdn suffix / weidian `?w=`), yielding the
 * original full-resolution source URL. Used by "search by this image" so the
 * upload uses the original (a thumbnail would degrade similarity).
 *
 * URLs that aren't CDN-rewritten (or are malformed) are returned unchanged.
 */
export function originalImageUrl(url: string): string {
  if (!url) return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const rule = platformRules().find((r) => r.cdnHost && r.cdnHost === parsed.hostname);
  if (!rule) return url;
  const sourceHost = parsed.searchParams.get(SOURCE_HOST_PARAM);
  parsed.hostname = sourceHost && rule.matchesSource(sourceHost) ? sourceHost : rule.sourceHost;
  parsed.searchParams.delete(SOURCE_HOST_PARAM);
  if (rule.kind === "alicdn") {
    parsed.pathname = stripAlicdnVariant(parsed.pathname);
  } else {
    // We always overwrite `w` on forward, so any `w` here is ours to drop.
    parsed.searchParams.delete("w");
  }
  return parsed.toString();
}
